import { Link, Stack } from "expo-router";
import { useMemo, useState } from "react";
import {
  ActivityIndicator,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";

import { space, textStyle } from "@passfirst/ui";

import { Button } from "../src/components/button";
import { AboutSection } from "../src/settings/components/about-section";
import { AccountSection } from "../src/settings/components/account-section";
import { DangerZoneSection } from "../src/settings/components/danger-zone-section";
import { NotificationsSection } from "../src/settings/components/notifications-section";
import { Section } from "../src/settings/components/section";
import { SessionSection } from "../src/settings/components/session-section";
import { SubscriptionsSection } from "../src/settings/components/subscriptions-section";
import { ThemeToggle } from "../src/settings/components/theme-toggle";
import { TabScaffold } from "../src/tab-scaffold";
import { type Colors, useColors } from "../src/theme";
import { useEffectiveSession } from "../src/use-effective-session";
import { useResponsive } from "../src/use-responsive";
import {
  useSelectedVertical,
  verticalDisplayLabel,
} from "../src/use-selected-vertical";

// Account / settings surface. Brief: split account-level surfaces into
// "Account" (always visible) and "Advanced" (one tap deeper). V1
// covers identity, theme, subscriptions, sign out, and account
// deletion (App Store requires the latter to be reachable and complete
// in-app). Data export remains backlogged.
export default function Settings(): JSX.Element {
  const session = useEffectiveSession();
  const selected = useSelectedVertical();
  const colors = useColors();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const { isWide } = useResponsive();
  // Manage-subscription errors bubble up here from the section so all
  // top-level error UX renders consistently in one place.
  const [error, setError] = useState<string | null>(null);

  if (session.isPending) {
    return (
      <View style={styles.center}>
        <ActivityIndicator />
      </View>
    );
  }

  if (!session.user) {
    return (
      <View style={styles.center}>
        <Text style={styles.emptyTitle}>Not signed in</Text>
        <Link href="/login" asChild>
          <Button label="Log in" fullWidth />
        </Link>
      </View>
    );
  }

  const subscriptionEntitlements = selected.entitlements
    .filter((e) => e.isSubscription)
    .map((e) => ({
      purchaseId: e.purchaseId,
      verticalName: e.verticalName,
      jurisdictionName: e.jurisdictionName,
    }));

  // One-off purchases grant lifetime access — they're not subscriptions,
  // so the Subscriptions section would otherwise read "No active
  // subscriptions" even though the user owns the credential. Surface them
  // explicitly so ownership is never misrepresented.
  const lifetimeEntitlements = selected.entitlements
    .filter((e) => !e.isSubscription)
    .map((e) => ({
      purchaseId: e.purchaseId,
      label: `${e.verticalName}${e.jurisdictionName ? ` — ${e.jurisdictionName}` : ""}`,
    }));

  const account = (
    <AccountSection
      email={session.user.email}
      emailVerified={session.user.emailVerified}
    />
  );
  const appearance = (
    <Section title="Appearance">
      <ThemeToggle />
    </Section>
  );
  const subscription = (
    <SubscriptionsSection
      isLoading={selected.isLoading}
      subscriptions={subscriptionEntitlements}
      onError={setError}
    />
  );
  const lifetime =
    !selected.isLoading && lifetimeEntitlements.length > 0 ? (
      <View style={styles.lifetimeBlock}>
        {lifetimeEntitlements.map((e) => (
          <Text key={e.purchaseId} style={styles.lifetimeText}>
            Lifetime access — {e.label}
          </Text>
        ))}
      </View>
    ) : null;
  const errorRow = error ? <Text style={styles.error}>{error}</Text> : null;

  return (
    <TabScaffold
      userLabel={session.user.email}
      metaLabel={verticalDisplayLabel(selected)}
    >
      <Stack.Screen options={{ title: "Settings" }} />
      <ScrollView
        contentContainerStyle={isWide ? styles.scrollWide : styles.scroll}
      >
        <Text style={styles.title}>Settings</Text>

        {isWide ? (
          // Web: two-column card grid (design_handoff_2 Rule B), max 820.
          <View style={styles.grid}>
            <View style={styles.gridCol}>
              {account}
              {appearance}
              {subscription}
              {lifetime}
              {errorRow}
            </View>
            <View style={styles.gridCol}>
              <SessionSection />
              <NotificationsSection />
              <AboutSection />
              <DangerZoneSection />
            </View>
          </View>
        ) : (
          <>
            {account}
            {appearance}
            {subscription}
            {lifetime}
            {errorRow}
            <SessionSection />
            <NotificationsSection />
            <AboutSection />
            <DangerZoneSection />
          </>
        )}
      </ScrollView>
    </TabScaffold>
  );
}

function makeStyles(colors: Colors) {
  return StyleSheet.create({
    scroll: {
      padding: space[6],
      maxWidth: 520,
      alignSelf: "center",
      width: "100%",
      paddingBottom: space[12],
    },
    scrollWide: {
      padding: space[10],
      maxWidth: 820,
      width: "100%",
      paddingBottom: space[12],
    },
    grid: {
      flexDirection: "row",
      gap: space[6],
      alignItems: "flex-start",
    },
    gridCol: { flex: 1, gap: space[5] },
    center: {
      flex: 1,
      backgroundColor: colors.background,
      alignItems: "center",
      justifyContent: "center",
      padding: space[6],
      gap: space[3],
    },
    title: {
      ...textStyle.h2,
      color: colors.heading,
      marginBottom: space[6],
    },
    emptyTitle: {
      ...textStyle.h3,
      color: colors.textPrimary,
    },
    error: {
      ...textStyle.bodySmall,
      color: colors.error,
      textAlign: "center",
    },
    lifetimeBlock: { gap: space[1], marginTop: -space[2] },
    lifetimeText: {
      ...textStyle.bodySmall,
      color: colors.textSecondary,
    },
  });
}
